import { useQuery } from "@tanstack/react-query";

import { ApiError } from "../../api/client";
import { FOCUS_KEYS, focusApi, type TimerSnapshot } from "../../api/focus";
import { useNow } from "./useNow";

/** Backstop poll; live frames (`focus:<owner>`) carry every real change. */
const POLL_MS = 60_000;

export interface FocusTimer {
  snapshot: TimerSnapshot | undefined;
  /** A focus or break is on the clock. */
  running: boolean;
  /** Milliseconds left on the server's clock, null while idle. */
  remainingMs: number | null;
  /** Reached 0:00 and the server hasn't advanced yet. */
  wrappingUp: boolean;
  /** The feature isn't there (404/403) — render nothing. */
  unavailable: boolean;
  isLoading: boolean;
}

/**
 * The timer snapshot without a ticking clock — for views that only need the
 * phase or session (no countdown), so they don't re-render twice a second.
 */
export function useTimerQuery() {
  return useQuery({
    queryKey: FOCUS_KEYS.timer,
    queryFn: focusApi.timer,
    refetchInterval: POLL_MS,
    retry: (count, error) =>
      !(error instanceof ApiError && [401, 403, 404].includes(error.status)) && count < 2,
  });
}

/**
 * The shared timer state (one `["focus","timer"]` cache entry for the chip
 * and the Today view). Never counts ticks: remaining time is
 * `planned_end_at − (Date.now() + clock_offset_ms)`, re-derived on a 500 ms
 * clock, so a throttled background tab is exact again on its next frame.
 */
export function useFocusTimer(): FocusTimer {
  const query = useTimerQuery();
  const snapshot = query.data;
  const session = snapshot && snapshot.phase !== "idle" ? snapshot.session : null;
  const now = useNow(500, session !== null);
  const remainingMs = session
    ? Date.parse(session.planned_end_at) - (now + (snapshot?.clock_offset_ms ?? 0))
    : null;
  const unavailable =
    query.error instanceof ApiError && [403, 404].includes(query.error.status) && !snapshot;
  return {
    snapshot,
    running: session !== null,
    remainingMs,
    wrappingUp: remainingMs !== null && remainingMs <= 0,
    unavailable,
    isLoading: query.isLoading,
  };
}
